import { createContext, ReactNode, useState } from 'react';

import AlertModal, { AlertModalProps } from '../components/molecules/modal/AlertModal';
import ConfirmModal, { ConfirmModalProps } from '../components/molecules/modal/ConfirmModal';
import DeleteModal, { DeleteModalProps } from '../components/molecules/modal/DeleteModal';
import AgreementModal, { AgreementModalProps } from '../components/molecules/modal/agreement-modal/AgreementModal';
import CookieModal, { CookieModalProps } from '../components/molecules/modal/cookie-modal/CookieModal';

import Overlay from '../components/molecules/modal/Overlay';
import SkillData from '../types/skillData';

enum ModalType {
  Alert = 1,
  Confirm,
  DeleteSkill,
  Agreement,
  Cookie,
}

type DeleteSkillTypeAndProps = [ModalType.DeleteSkill, DeleteModalProps];
type ConfirmTypeAndProps = [ModalType.Confirm, ConfirmModalProps];
type AlertTypeAndProps = [ModalType.Alert, AlertModalProps];
type AgreementTypeAndProps = [ModalType.Agreement, AgreementModalProps];
type CookieTypeAndProps = [ModalType.Cookie, CookieModalProps];

type ModalTypeAndProps =
  | DeleteSkillTypeAndProps
  | ConfirmTypeAndProps
  | AlertTypeAndProps
  | AgreementTypeAndProps
  | CookieTypeAndProps;

interface ModalContextProps {
  showDeleteSkillModal: (skill: SkillData, onDelete: () => void) => void;
  showConfirmModal: (onConfirm: () => void, promptText?: string) => void;
  showAlertModal: (promptText?: string) => void;
  showAgreementModal: (initialActiveTabIndex?: number) => void;
  showCookieModal: () => void;
}

const ModalContext = createContext<ModalContextProps>({
  showDeleteSkillModal() {
    throw new Error('ModalContext must be used within the ModalProvider');
  },
  showConfirmModal() {
    throw new Error('ModalContext must be used within the ModalProvider');
  },
  showAlertModal() {
    throw new Error('ModalContext must be used within the ModalProvider');
  },
  showAgreementModal() {
    throw new Error('ModalContext must be used within the ModalProvider');
  },
  showCookieModal() {
    throw new Error('ModalContext must be used within the ModalProvider');
  },
});

interface ModalProviderProps {
  children?: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [modalTypeAndProps, setModalTypeAndProps] = useState<ModalTypeAndProps>();
  const [modalType, modalProps] = modalTypeAndProps ? modalTypeAndProps : [undefined, undefined];

  const showDeleteSkillModal = (skill: SkillData, onDelete: () => void) => {
    setModalTypeAndProps([ModalType.DeleteSkill, { skill, onDelete, onClose: () => setModalTypeAndProps(undefined) }]);
  };

  const showConfirmModal = (onConfirm: () => void, promptText?: string) => {
    setModalTypeAndProps([
      ModalType.Confirm,
      {
        onConfirm: () => {
          onConfirm();
          setModalTypeAndProps(undefined);
        },
        promptText,
        onClose: () => setModalTypeAndProps(undefined),
      },
    ]);
  };

  const showAlertModal = (promptText?: string) => {
    setModalTypeAndProps([ModalType.Alert, { promptText, onClose: () => setModalTypeAndProps(undefined) }]);
  };

  const showAgreementModal = (initialActiveTabIndex?: number) => {
    setModalTypeAndProps([
      ModalType.Agreement,
      { onClose: () => setModalTypeAndProps(undefined), initialActiveTabIndex },
    ]);
  };

  const showCookieModal = (onAcceptAll?: () => void, onAcceptRequired?: () => void, onAcceptSelected?: () => void) => {
    setModalTypeAndProps([
      ModalType.Cookie,
      { onClose: () => setModalTypeAndProps(undefined), onAcceptAll, onAcceptRequired, onAcceptSelected },
    ]);
  };

  const showFunctions = {
    showDeleteSkillModal,
    showConfirmModal,
    showAlertModal,
    showAgreementModal,
    showCookieModal,
  };

  if (!modalType) {
    return <ModalContext.Provider value={showFunctions}>{children}</ModalContext.Provider>;
  }

  return (
    <ModalContext.Provider value={showFunctions}>
      {children}
      <Overlay>
        {modalType === ModalType.DeleteSkill && <DeleteModal {...modalProps} />}
        {modalType === ModalType.Confirm && <ConfirmModal {...modalProps} />}
        {modalType === ModalType.Alert && <AlertModal {...modalProps} />}
        {modalType === ModalType.Agreement && <AgreementModal {...modalProps} />}
        {modalType === ModalType.Cookie && <CookieModal {...modalProps} />}
      </Overlay>
    </ModalContext.Provider>
  );
}

export default ModalContext;
